<template>
  <div class="explorer-panel-container">
    <div class="explorer-panel">
      <header>
        <div class="title">
          <span v-if="isLoadingDropboxes" class="icon hoverable">
            <i class="fa fa-spinner fa-spin fa-sm" />
          </span>
          <span v-else>
            <span class="icon"><i class="fa fa-folder" /></span>
          </span>
          <div
            class="select is-small"
            :class="{
              'is-danger': !isLoadingDropboxes && !dropbox,
            }"
          >
            <select
              v-model="selectedDropboxId"
              :disabled="dropboxes.length === 0 || lockRunDir || lockDropbox"
            >
              <option :value="null">Select dropbox</option>
              <option
                v-for="dropboxOpt in dropboxes"
                :key="`dropbox-opt-${dropboxOpt.id}`"
                :value="dropboxOpt.id"
              >
                {{ dropboxOpt.path }}
              </option>
            </select>
          </div>
          <div style="margin-left: auto">
            <slot name="controls" :reload-dropboxes="loadDropboxes"></slot>
          </div>
        </div>
      </header>
      <section class="fit-height">
        <template v-if="isLoadingDropboxes">
          <div
            style="display: flex; width: 100%"
            :style="{ height: isLoadingDropboxes ? '100%' : 'auto' }"
          >
            <span class="icon is-large" style="margin: auto">
              <i class="fa fa-spinner fa-spin fa-2x" />
            </span>
          </div>
        </template>
        <div
          v-else-if="dropboxes.length === 0"
          class="notification is-fullwidth is-warning m-4"
        >
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          <span class="text">
            No user dropbox has been set up, please contact your admin
          </span>
        </div>
        <template v-else-if="dropbox && !dropbox.isEmpty">
          <file-explorer
            :root-folder="processedDropbox"
            :fetch-folder-content="getFolderContent"
            :select-left-folder="setDropboxSelectedFolder"
            :selected-left-folder="dropboxSelectedFolder"
            :lock-selected-left-folder="dropboxIsAssayRunDirRoot"
            :select-data="setDropboxSelectedFiles"
            :selected-data="dropboxSelectedData"
            :selected-files-folder-uri="selectedFilesFolderUri"
            :selected-files-folder-name="selectedFilesFolderName"
            :reset-selected-files="resetDropboxSelectedFiles"
            :can-select-root-folder="canSelectRootFolder"
          >
            <template v-if="lockRunDir" #notifications>
              <div
                class="notification is-info is-light m-0 is-flex-grow-0"
                style="border-radius: 0"
              >
                <span class="icon">
                  <i class="fa-solid fa-info" />
                </span>
                <span>The run directory of this assay cannot be changed</span>
              </div>
            </template>
            <template #explorer-options>
              <div>
                <div
                  class="is-flex is-align-items-baseline is-flex-wrap-wrap px-2"
                >
                  <div class="is-flex is-align-items-baseline mr-5">
                    <span class="icon">
                      <i class="fa fa-cog fa-xs" />
                    </span>
                    Transfer Strategy
                  </div>
                  <div class="is-flex is-align-items-baseline">
                    <div class="is-flex is-flex-direction-column">
                      <div
                        class="select is-small"
                        :class="{ 'is-danger': transferWholeInputDir === null }"
                      >
                        <select
                          :value="String(transferWholeInputDir)"
                          :disabled="lockRunDir"
                          @change="selectTransferStrategy"
                        >
                          <option disabled value="null">-- select --</option>
                          <option value="true">
                            Transfer the entire run folder
                          </option>
                          <option value="false">
                            Transfer selected files and folders only
                          </option>
                        </select>
                      </div>
                      <div
                        v-if="transferWholeInputDir === null"
                        class="help has-text-right has-text-danger px-1"
                      >
                        Please select a transfer strategy
                      </div>
                    </div>
                    <span
                      v-tooltip="{
                        content: transferStrategyHelp,
                        html: true,
                      }"
                      class="icon hoverable-icon has-text-primary ml-2"
                    >
                      <i class="fas fa-info-circle" />
                    </span>
                  </div>
                </div>
                <div
                  v-if="
                    transferWholeInputDir === true ||
                    transferWholeInputDir == false
                  "
                  class="help has-text-success has-text-weight-bold mx-2 mt-2"
                >
                  <p v-if="transferWholeInputDir === true">
                    The entire run folder
                    <code
                      v-if="dropboxSelectedFolder.name"
                      class="has-text-primary p-1 mr-1 ml-1"
                    >
                      {{ dropboxSelectedFolder.name }}/
                    </code>
                    will be transferred. However, only the selected files and
                    folders will be registered as
                    <b>datasets</b>
                  </p>
                  <p v-else-if="transferWholeInputDir === false">
                    Only files and folders selected within the run folder
                    <code
                      v-if="dropboxSelectedFolder.name"
                      class="has-text-primary p-1 mr-1 ml-1"
                    >
                      {{ dropboxSelectedFolder.name }}/
                    </code>
                    will be transferred. Additional content will be ignored.
                  </p>
                </div>
              </div>
            </template>
            <template #explorer-right-info>
              Explore
              <b class="has-text-primary">run folders</b>
              and select
              <b class="has-text-primary">dataset(s)</b>
              to register
            </template>
            <template #tree-label="{ classes }">
              <section :class="classes" class="py-2">
                <span>
                  Select individual datasets,
                  <em>i.e.</em>
                  files and/or folders
                </span>
                <span
                  v-tooltip="
                    `When the selected item is a folder, all its content will be transferred and registered as one dataset.`
                  "
                  class="icon hoverable-icon has-text-primary ml-2"
                >
                  <i class="fa fa-info fa-sm" />
                </span>
              </section>
            </template>
          </file-explorer>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import {
  fetchDropboxes,
  getDropboxFolderContent,
} from '@/utils/api/dataManagement'
import { removeRootPathFromUri } from '@/utils'
import FileExplorer from '@/components/FileExplorer.vue'

// function computeRelativeLocations(root, contentArray) {
//   return contentArray.map((c) => {
//     const relativePath = removeRootPathFromUri(root, c.uri)
//     return { ...c, relativePath }
//   })
// }

export default {
  components: {
    FileExplorer,
  },
  name: 'Databox',
  emits: ['is-loading', 'change-dropbox', 'change-folder', 'loading-dropboxes'],
  data() {
    return {
      dropboxes: [],
      isLoadingDropboxes: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['loggedInUser']),
    ...mapGetters('dataloader/assay', {
      assayId: 'assayId',
      assayType: 'resolvedAssayType',
      assayRunDir: 'assayRunDir',
      assayIsNew: 'assayIsNew',
      assayIsInitialized: 'assayIsInitialized',
      assayIsRegistered: 'assayIsRegistered',
      assayHasRunDir: 'assayHasRunDir',
    }),
    ...mapState('dataloader/dropbox', {
      dropbox: (state) => state.data,
      transferWholeInputDir: (state) => state.transferWholeInputDir,
      dropboxSelectedFolder: (state) => state.selectedFolder,
      dropboxSelectedData: (state) =>
        Object.values(state.selectedFiles).map((f) => f.relativePath),
    }),
    ...mapGetters('dataloader/dropbox', [
      'selectedFilesFolderUri',
      'selectedFilesFolderName',
    ]),
    includeRunDirectories() {
      let includeRunDir = false
      // Include run directories:
      // 1. When the user is loading raw data to a new assay
      // 1.1. The user already has created an assay (we have an assay ID and
      // the status of the assay is "NEW")
      // 1.2. The user will be creating the assay as part of the loading
      // process. In this case we always have the assay type anyways.
      // This points  eventually falls back to 1.1. if user navigates back to
      // data selection after creating the assay.
      // 2. When the user is loading raw data into an "INITIALIZED" assay. The
      // run dir already has been set, we want to auto select it (so it should
      // be included in the dropbox response)
      const isLoadingRawDataIntoAssay =
        (this.assayId && (this.assayIsNew || this.assayIsInitialized)) ??
        !!this.assayType
      // Exclude run directories:
      // 1. When the assay has the status REGISTERED. Derived data can be loaded
      // but never from run directories anyways.
      // 2. When the user is loading data unrelated to an assay
      // When there is no assay id, we do not want to include run directories
      // When the assay is REGISTERED: The run dir is set on the assay and
      // we do not want user to be able to select more data from this run dir
      const isLoadingDataWithoutAssay = !this.assayType
      const isLoadingDerivedData =
        (this.assayId && this.assayIsRegistered) ?? isLoadingDataWithoutAssay

      includeRunDir = isLoadingRawDataIntoAssay ?? !isLoadingDerivedData
      return includeRunDir
    },
    // getDropboxUrlParams() {

    // },
    primaryGroupId() {
      return this.loggedInUser.primaryGroup?.id
    },
    selectedDropboxId: {
      get() {
        return this.dropbox?.id || null
      },
      set(id) {
        const dropbox = this.getDropboxById(id)
        this.setDropbox(dropbox)
      },
    },
    processedDropbox() {
      return {
        ...this.dropbox,
        files: this.dropbox.files.map((f) => {
          return {
            ...f,
            isSelectable: !this.lockRunDir && !f.is_registered,
          }
        }),
      }
    },
    dropboxByAssayRunDir() {
      const dropbox =
        this.assayRunDir &&
        this.dropboxes?.find((d) => this.assayRunDir.startsWith(d?.path))
      return dropbox
    },
    dropboxIsAssayRunDirRoot() {
      return (
        (this.dropboxByAssayRunDir &&
          this.dropboxByAssayRunDir?.id === this.selectedDropboxId) ||
        false
      )
    },
    defaultDropboxId() {
      let id = this.getDropboxByGroupId(this.primaryGroupId)?.id
      if (this.assayIsInitialized && this.dropboxByAssayRunDir?.id)
        id = this.dropboxByAssayRunDir.id
      return id
    },
    lockRunDir() {
      return this.includeRunDirectories && this.assayHasRunDir
    },
    lockDropbox() {
      return this.lockRunDir
    },
    dropboxAssayRunDir() {
      return this.dropboxByAssayRunDir?.files?.find((f) =>
        this.assayRunDir.includes(f.relativePath),
      )
    },
    isUserDropbox() {
      return !!this.dropbox?.is_user_dropbox
    },
    canSelectRootFolder() {
      return this.isUserDropbox
    },
    transferStrategyHelp() {
      const folder = this.dropboxSelectedFolder?.name
      const folderCode = folder ? `<code>${folder}/</code>` : ''
      return `<p>You can choose from two different data transfer strategies:</p>
        <ul>
          <li><u>Transfer the entire run folder:</u>
            The entire <b>run folder</b> ${folderCode} will be transferred.
            However, only the selected files and folders will be registered
            as <b>datasets.</b> Use this option if your <b>run folder</b>
            contains important supporting data that does not qualify as dataset.</li>
          <li><u>Only transfer selected files and folders:</u>
            Only the selected datasets are transferred. Additional content
            found in the <b>run folder</b> ${folderCode} will be ignored.</li>
        </ul>`
    },
  },
  methods: {
    ...mapMutations('dataloader/dropbox', {
      setDropbox: 'setDropbox',
      setDropboxSelectedFolder: 'setSelectedFolder',
      resetDropboxSelectedFolder: 'resetSelectedFolder',
      setDropboxSelectedFiles: 'setSelectedFiles',
      resetDropboxSelectedFiles: 'resetSelectedFiles',
      setTransferWholeInputDir: 'setTransferWholeInputDir',
    }),
    selectTransferStrategy(event) {
      const transferType =
        event?.target.value === 'true'
          ? true
          : event?.target.value === 'false'
          ? false
          : null
      this.setTransferWholeInputDir(transferType)
    },
    autoSelectDropbox() {
      this.selectedDropboxId = this.defaultDropboxId
    },
    async getDropboxes() {
      let params = new URLSearchParams()
      params.set('include_run_dirs', this.includeRunDirectories)
      if (this.assayId) params.set('assay_id', this.assayId)

      let dropboxes = await fetchDropboxes(params)
      dropboxes = dropboxes.map((d) => {
        const { files: dropboxFiles, path: dropboxRoot, url: dropboxUrl } = d
        return {
          ...d,
          // compute url to get its content
          fetchContentUrl: `${dropboxUrl}files/?folder=`,
          // compute relative paths for files in dropbox, based on dropbox root
          files: dropboxFiles.map((f) => ({
            ...f,
            relativePath: removeRootPathFromUri(dropboxRoot, f.uri),
          })),
        }
      })
      return dropboxes
    },
    async loadDropboxes() {
      this.isLoadingDropboxes = true
      this.dropboxes = await this.getDropboxes(this.getDropboxUrlParams)
      this.autoSelectDropbox()
      this.isLoadingDropboxes = false
    },
    getDropboxById(id) {
      return this.dropboxes?.find((d) => d?.id === id)
    },
    getDropboxByGroupId(id) {
      return this.dropboxes?.find((d) => d.group.value?.id === id)
    },
    async getFolderContent(folder) {
      return getDropboxFolderContent(this.dropbox, folder)
    },
    autoSelectAssayRunDir() {
      if (this.dropboxIsAssayRunDirRoot)
        this.setDropboxSelectedFolder(this.dropboxAssayRunDir)
    },
  },
  async mounted() {
    this.$emit('is-loading', true)
    await this.loadDropboxes()
    this.$emit('is-loading', false)
  },
  watch: {
    dropbox: {
      handler: function (d, oldd) {
        const _selectedDropboxId = (d && d?.id) || null
        const sameDropbox = _selectedDropboxId === oldd?.id
        if (!sameDropbox) {
          this.$emit('change-dropbox', _selectedDropboxId)
          this.resetDropboxSelectedFolder()
        }
        if (_selectedDropboxId) this.autoSelectAssayRunDir()
      },
      deep: true,
    },
    dropboxSelectedFolder: {
      handler: function (newFolder, oldFolder) {
        this.setTransferWholeInputDir(this.assayHasRunDir || null)
        const newFolderId = newFolder?.id
        const oldFolderId = oldFolder?.id
        if (newFolderId !== oldFolderId) {
          this.$emit('change-folder')
        }
      },
      deep: true,
    },
    isLoadingDropboxes(loading) {
      this.$emit('loading-dropboxes', loading)
    },
  },
}
</script>

<style scoped lang="scss">
.explorer-panel-containter {
  height: 100%;
}
.explorer-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.explorer-panel > header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  background-color: var(--color-background-default);
}

.explorer-panel > header > .title {
  display: flex;
  align-items: center;
  font-size: 1em;
  margin: 0.5rem 0;
  padding: 0 0.5rem;
  width: 100%;
}

.explorer-panel > header > .title .select {
  overflow: hidden;
}

.explorer-panel > header > .title .select > select {
  text-overflow: ellipsis;
}

.explorer-panel > header > .title .icon {
  margin: 0 0.5rem;
}

.fit-height {
  height: 100%;
  overflow: hidden;
}
</style>
